import { type FC, useState } from "react";
import {
  AppBar,
  Box,
  Card,
  CircularProgress,
  TablePagination,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useCollectionsQuery } from "@/partner/entities/contents";
import { ContentTypeType } from "@/types/graphql/partner.generated";
import { useFilter } from "@/partner/shared/components/Filter";
import { EmptyListCard } from "@/partner/shared/components/EmptyListCard";
import { CollectionsListItem } from "./CollectionsListItem";
import { CollectionsListToolbar } from "./CollectionsListToolbar";
import type { SelectedCollectionItem } from "./CollectionsListToolbar";

interface CollectionsListProps {
  onEditCollection: (id: string) => void;
}

// Порт референсного CollectionsList: карточки (не таблица) с чекбоксами,
// тулбар массовых действий, пагинация.
export const CollectionsList: FC<CollectionsListProps> = ({
  onEditCollection,
}) => {
  const { t } = useTranslation();
  const {
    onPageChange,
    onRowsPerPageChange,
    params: { page, rowsPerPage },
    filter: { search, tagIds, status, collectionIds, productIds },
  } = useFilter();

  const { data, loading } = useCollectionsQuery({
    variables: {
      page,
      rowsPerPage,
      filter: {
        type: ContentTypeType.CollectionType,
        search,
        tagIds,
        status: status || undefined,
        collectionIds,
        productIds,
      },
    },
    fetchPolicy: "cache-and-network",
  });

  const collections = data?.contents?.nodes ?? [];
  const count = data?.contents?.count ?? 0;

  const [selectedItems, setSelectedItems] = useState<SelectedCollectionItem[]>(
    []
  );
  const selectedIds = selectedItems.map((item) => item.id);

  // Фикс референсного бага: в contentToSelected коллекций не прокидывались
  // collectionIds — чекбоксы AddToCollectionButton стартовали пустыми.
  const toSelected = (collection: {
    id: string;
    collectionIds: string[];
    productIds: string[];
  }): SelectedCollectionItem => ({
    id: collection.id,
    collectionIds: collection.collectionIds,
    productIds: collection.productIds,
  });

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedItems(collections.map(toSelected));
    } else {
      setSelectedItems([]);
    }
  };

  const handleSelectOne = (
    id: string,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    event.stopPropagation();
    if (selectedIds.includes(id)) {
      setSelectedItems(selectedItems.filter((item) => item.id !== id));
    } else {
      const collection = collections.find((item) => item.id === id);
      if (collection) {
        setSelectedItems([...selectedItems, toSelected(collection)]);
      }
    }
  };

  const numSelected = selectedIds.length;
  const indeterminate = numSelected > 0 && numSelected < collections.length;

  if (loading && !data) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (collections.length === 0) {
    return <EmptyListCard title={t("collections.list.empty")} />;
  }

  return (
    <Box>
      <Card elevation={0}>
        <AppBar position="static" color="default" elevation={0}>
          <CollectionsListToolbar
            indeterminate={indeterminate}
            numSelected={numSelected}
            totalCount={collections.length}
            onSelectAllClick={handleSelectAll}
            selectedItems={selectedItems}
            onCloseSelected={() => setSelectedItems([])}
          />
        </AppBar>
      </Card>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 1 }}>
        {collections.map((collection) => (
          <CollectionsListItem
            key={collection.id}
            collection={collection}
            selected={selectedIds.includes(collection.id)}
            onSelectOneChange={handleSelectOne}
            onClickItem={onEditCollection}
          />
        ))}
      </Box>

      <TablePagination
        component="div"
        count={count}
        page={page}
        onPageChange={onPageChange}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={onRowsPerPageChange}
        rowsPerPageOptions={[5, 10, 25, 100]}
        labelRowsPerPage={t("common.pagination.rowsPerPage")}
        labelDisplayedRows={({ from, to, count }) =>
          t("common.pagination.displayedRows", {
            from,
            to,
            count:
              count !== -1 ? count : t("common.pagination.moreThan", { to }),
          })
        }
      />
    </Box>
  );
};
